/**
 * AI opponent policy (HANDOFF §3.8) — Agent C (cards/AI).
 *
 * PURE given state + rng, parameterised by difficulty (AI_PARAMS in
 * ./constants.ts) so it stays swappable for a real remote opponent later (§4.5)
 * without touching game rules. NO DOM. ALL randomness via the injected rng.
 *
 * PHASE 1 CONTRACT — Agent A calls `aiDecide` once per Tick from the reducer and
 * feeds the returned actions back through the reducer. The policy NEVER mutates
 * state directly; it only emits intent {@link Action}s (or, where the current
 * Action set cannot express a rival-side intent, surfaces the decision through
 * the exported pure helpers below — see the INTEGRATION NOTE).
 *
 * ───────────────────────────────────────────────────────────────────────────
 * INTEGRATION NOTE (reducer/contract mismatch — flagged for Agent A / overseer)
 * ───────────────────────────────────────────────────────────────────────────
 * The §3.8 AI is the `rival` side. Driving it through the reducer needs an action
 * that (a) targets the RIVAL and (b) resets the rival's attack/defense timer when
 * it fires. The current {@link Action} union + reducer handlers cannot express
 * either:
 *   - `PlayOffense` / `PlayDefense` are hardcoded to `'you'` in game.ts's
 *     `applyAction` (`startAim(state, 'you', …)`, `playDefense(state, 'you', …)`)
 *     and there is no `side` field on those actions, so returning them from
 *     `aiDecide` would hijack the HUMAN's swing/defense, not the rival's.
 *   - The reducer decrements `ai.attackTimer` / `ai.defenseTimer` itself
 *     (`advanceAiTimers`) but exposes no action to RESET them, so the AI would
 *     re-fire every tick once a timer crosses zero.
 * Because emitting any current action would corrupt the human side, `aiDecide`
 * returns `[]` (the only contract-safe value under today's reducer) while the
 * FULL, deterministic, difficulty-scaled decision lives in {@link decideAi} /
 * {@link decideOffense} / {@link decideDefense} below (exported + unit-tested).
 * Wiring is a one-line change once Agent A adds a rival-capable action — see
 * {@link aiActionsFromDecision} for the exact intent shape the policy produces.
 */
import type {
  Action,
  AiState,
  Club,
  DefenseCard,
  Difficulty,
  GameState,
  Green,
  Rng,
  Side,
  SideState,
  Vec2,
} from './types.ts';
import { AI_PARAMS, DEG_TO_RAD, CARD_MAP, SHOT_RANGE, boxForPower, type AiParams } from './constants.ts';

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

function bearing(from: Vec2, to: Vec2): number {
  return Math.atan2(to.y - from.y, to.x - from.x);
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/** The side the AI controls. The human is always `you`; the AI is `rival`. */
export const AI_SIDE: Side = 'rival';

/** The opponent of the AI (the green it attacks / the tees it harasses). */
export const AI_TARGET_SIDE: Side = 'you';

/** Affordable cards in a hand, given current (floored) energy (§3.6). */
function affordableIndices<T extends { readonly cost: number }>(
  hand: readonly T[],
  energy: number,
): number[] {
  const floored = Math.floor(energy);
  const out: number[] = [];
  for (let i = 0; i < hand.length; i++) {
    const card = hand[i];
    if (card !== undefined && card.cost <= floored) out.push(i);
  }
  return out;
}

/** Pick a uniformly-random element index from the injected rng. */
function pickIndex(length: number, rng: Rng): number {
  if (length <= 0) return -1;
  return Math.min(length - 1, Math.max(0, Math.floor(rng() * length)));
}

// ---------------------------------------------------------------------------
// Offense decision (§3.8): pick a club, aim at the pin with jitter + shank,
// power with jitter, weakened when the launching tee box is rough.
// ---------------------------------------------------------------------------

/** A resolved AI offensive shot intent (everything the reducer needs to fire it). */
export interface AiShotIntent {
  readonly kind: 'shot';
  /** Index into the AI side's offense hand. */
  readonly clubIdx: number;
  readonly club: Club;
  /** Launch point (the AI's own tee box for this club). */
  readonly origin: Vec2;
  /** Final launch angle (radians): aim-at-pin + jitter (+ shank, + rough spread). */
  readonly angle: number;
  /** Launch power, clamped per difficulty (and weakened if the tee is rough). */
  readonly power: number;
}

/**
 * Decide the AI's offensive shot for this attack tick (§3.8), or `null` to skip.
 *
 * Mirrors the prototype `aiTick` attack branch, generalised to difficulty params:
 *  - hesitation: with probability `params.skipChance`, skip (do nothing).
 *  - random affordable club; aim at the TARGET pin with base spread × difficulty
 *    multiplier, plus an occasional gross shank (`shankChance`/`shankMagnitude`).
 *  - power = `powerBase ± powerJitter/2`, clamped to [`powerMin`, `powerMax`].
 *  - if the launching tee box is rough (§3.7), the AI is weakened exactly like the
 *    player: power × `roughPowerMultiplier`, +`roughSpreadDegrees` extra spread.
 *
 * Deterministic given the rng. Draw ORDER is fixed so a seed reproduces the shot.
 */
export function decideOffense(
  aiState: SideState,
  targetGreen: Green,
  params: AiParams,
  rng: Rng,
): AiShotIntent | null {
  const affordable = affordableIndices(aiState.offenseHand, aiState.energy);
  if (affordable.length === 0) return null;

  // Hesitation ("skip chance"): the AI sometimes does nothing this tick (§3.8).
  if (rng() < params.skipChance) return null;

  const clubIdx = affordable[pickIndex(affordable.length, rng)] as number;
  const club = aiState.offenseHand[clubIdx] as Club;
  const tee = aiState.tees[boxForPower(club.power)]; // POWER picks the launch tee
  const origin = tee.pos;
  const rough = tee.roughRemaining > 0; // only THIS launch spot may be roughed (§3.7, per-tee)

  // Power: DIAL to the hole's fraction h (= pinDist / the tee's SET range) ± jitter, then
  // clamp; rough weakens it (§3.7). (SET-range model: a clean swing no longer auto-finds the pin.)
  const pinDist = Math.hypot(origin.x - targetGreen.pin.x, origin.y - targetGreen.pin.y);
  const range = SHOT_RANGE[boxForPower(club.power)];
  const h = range > 0 ? pinDist / range : 1;
  let power = h + (rng() - 0.5) * params.powerJitter;
  let spreadDeg = CARD_MAP.spreadDeg(club.accuracy) * params.spreadMultiplier;
  if (rough) {
    power *= params.roughPowerMultiplier;
    spreadDeg += params.roughSpreadDegrees;
  }
  power = clamp(power, 0.1, 1);

  // Aim at the pin + base spread wobble + an occasional gross shank (§3.8).
  const aim = bearing(origin, targetGreen.pin);
  const wobble = (rng() - 0.5) * (spreadDeg * DEG_TO_RAD);
  const shank = rng() < params.shankChance ? (rng() - 0.5) * params.shankMagnitude : 0;
  const angle = aim + wobble + shank;

  return { kind: 'shot', clubIdx, club, origin, angle, power };
}

// ---------------------------------------------------------------------------
// Defense decision (§3.8): pick a random affordable defensive card to play.
// ---------------------------------------------------------------------------

/** A resolved AI defensive intent: which defense card (by hand index) to play. */
export interface AiDefenseIntent {
  readonly kind: 'defense';
  /** Index into the AI side's defense hand. */
  readonly cardIdx: number;
  readonly card: DefenseCard;
}

/**
 * Decide the AI's defensive play for this defense tick (§3.8), or `null` to skip.
 *
 * Mirrors the prototype `aiTick` defense branch: a RANDOM affordable defensive
 * card. The actual effect (hazard defending its OWN green / rough the opponent's tee
 * / wind) is applied by {@link applyDefense} when the reducer plays it — the AI only
 * chooses the card here. A hazard card whose green is already full still "plays"
 * (charges + cycles) and no-ops the effect, matching the prototype guard.
 */
export function decideDefense(aiState: SideState, rng: Rng): AiDefenseIntent | null {
  const affordable = affordableIndices(aiState.defenseHand, aiState.energy);
  if (affordable.length === 0) return null;
  const cardIdx = affordable[pickIndex(affordable.length, rng)] as number;
  const card = aiState.defenseHand[cardIdx] as DefenseCard;
  return { kind: 'defense', cardIdx, card };
}

// ---------------------------------------------------------------------------
// Combined per-tick decision (§3.8)
// ---------------------------------------------------------------------------

/** The AI's decision for one tick: an optional shot and/or an optional defense. */
export interface AiDecision {
  /** Offensive shot to take this tick (attack timer fired), if any. */
  readonly shot: AiShotIntent | null;
  /** Defensive card to play this tick (defense timer fired), if any. */
  readonly defense: AiDefenseIntent | null;
}

const EMPTY_DECISION: AiDecision = { shot: null, defense: null };

/**
 * The complete §3.8 policy for one Tick, parameterised by `state.ai.difficulty`.
 *
 * Fires the offense branch only when `attackTimer <= 0` and the defense branch
 * only when `defenseTimer <= 0` (the reducer counts the timers down; see the
 * INTEGRATION NOTE for the missing reset action). The rng draw order is fixed —
 * attack branch first, then defense — so a seed reproduces the whole decision.
 *
 * Difficulty scales the policy purely through {@link AiParams} (AI_PARAMS): a
 * HARDER AI attacks more often (shorter cooldowns — owned by the reducer),
 * hesitates less (`skipChance ↓`), shanks less (`shankChance`/`shankMagnitude ↓`),
 * and jitters less (`spreadMultiplier`/`powerJitter ↓`). Easy is the mirror.
 */
export function decideAi(state: GameState, rng: Rng): AiDecision {
  if (state.phase !== 'playing' && state.phase !== 'playoff') return EMPTY_DECISION;

  const params = AI_PARAMS[state.ai.difficulty];
  const ai = state.rival;
  const targetGreen = state.you.green;

  const shot = state.ai.attackTimer <= 0 ? decideOffense(ai, targetGreen, params, rng) : null;
  const defense = state.ai.defenseTimer <= 0 ? decideDefense(ai, rng) : null;

  return { shot, defense };
}

/**
 * Whether a given AI state's timer windows are "armed" this tick — handy for the
 * reducer to know when it should RESET the timer after applying the decision.
 * (The reset window itself is `cooldownMin + rng * cooldownRange`; see AI_PARAMS.)
 */
export function attackArmed(ai: AiState): boolean {
  return ai.attackTimer <= 0;
}
export function defenseArmed(ai: AiState): boolean {
  return ai.defenseTimer <= 0;
}

/**
 * Project an {@link AiDecision} into the intent {@link Action}s the AI WOULD emit
 * to the reducer, ASSUMING a rival-capable action existed. Today the reducer's
 * `PlayOffense`/`PlayDefense` are `you`-only (see the INTEGRATION NOTE), so this
 * is the shape Agent A should wire to once a `side` is threaded through those
 * actions (or a dedicated rival-act action is added). Kept pure + exported so the
 * wiring — and its determinism — is unit-testable now, ahead of that change.
 */
export function aiActionsFromDecision(decision: AiDecision): readonly Action[] {
  const actions: Action[] = [];
  // NOTE: with today's `you`-bound handlers these would target the human; they
  // are returned by `aiDecide` ONLY once the reducer can route them to the rival.
  if (decision.defense) actions.push({ type: 'PlayDefense', cardIdx: decision.defense.cardIdx });
  if (decision.shot) actions.push({ type: 'PlayOffense', cardIdx: decision.shot.clubIdx });
  return actions;
}

// ---------------------------------------------------------------------------
// Contract entry point
// ---------------------------------------------------------------------------

/**
 * Decide the AI's actions for this step (§3.8) — the PHASE 1 CONTRACT entry point.
 *
 * Returns `[]` under TODAY's reducer because the {@link Action} union cannot
 * express a rival-side play (see the INTEGRATION NOTE at the top of this file):
 * emitting `PlayOffense`/`PlayDefense` would corrupt the HUMAN's turn, which is
 * worse than the AI standing still. The real, deterministic, difficulty-scaled
 * policy lives in {@link decideAi} (+ {@link aiActionsFromDecision}); once Agent A
 * adds a rival-capable action this becomes:
 *
 *     return aiActionsFromDecision(decideAi(state, rng));
 *
 * `_dt` is accepted for the contract signature; the reducer owns timer countdown.
 */
export function aiDecide(_state: GameState, _dt: number, _rng: Rng): readonly Action[] {
  return [];
}

/** Re-exported so the difficulty type is importable alongside the policy. */
export type { Difficulty };
